package app

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import business.defaultCampaignConfig
import business.effectiveApecCardCostRub
import business.googleSheetsLeadConfig
import calculator.calculateComparison
import calculator.defaultCalculationPresets
import calculator.publicRanking
import calculator.publicScenarios
import components.FormulaPanel
import components.FunnelState
import components.InputPanel
import components.LeadForm
import components.ReferralFunnel
import components.ReferralDiscountPanel
import components.ResultsSection
import components.SeoContent
import form.CalculatorFormState
import form.fromInput
import form.toCalculatorInputOrErrors
import format.formatRub
import org.jetbrains.compose.web.dom.Button
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.H1
import org.jetbrains.compose.web.dom.H2
import org.jetbrains.compose.web.dom.Li
import org.jetbrains.compose.web.dom.P
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text
import org.jetbrains.compose.web.dom.Ul
import services.browserMetricsService
import services.BrowserGoogleSheetsLeadService
import services.NoopFunnelEventService
import kotlinx.browser.window
import kotlinx.coroutines.launch
import kotlin.math.roundToLong
import services.FunnelEvent

@Composable
fun App() {
    var form by remember { mutableStateOf(CalculatorFormState.default()) }
    var leadForm by remember { mutableStateOf(LeadForm()) }
    var funnelState by remember { mutableStateOf(FunnelState()) }
    val campaignConfig = defaultCampaignConfig
    val metricsService = remember { browserMetricsService() }
    val sessionId = remember { browserSessionId() }
    val leadService = remember { BrowserGoogleSheetsLeadService(googleSheetsLeadConfig) }
    val eventService = remember { NoopFunnelEventService() }
    val scope = rememberCoroutineScope()
    LaunchedEffect(Unit) {
        metricsService.track("page_view", "landing", "", "", "")
    }

    val parsed = form.toCalculatorInputOrErrors()
    val fieldErrors = parsed.errors.associate { it.fieldKey to it.message }
    val effectiveInput = parsed.input?.let { input ->
        input.copy(
            apecCardCostRub = effectiveApecCardCostRub(
                baseCostRub = input.apecCardCostRub,
                discountRub = campaignConfig.referralDiscountRub,
                enabled = funnelState.referralDiscountEnabled,
            ),
        )
    }
    val comparison = effectiveInput?.let { calculateComparison(it) }
    val visibleScenarios = comparison?.let { publicScenarios(it.scenarios) }.orEmpty()
    val visibleRanking = comparison?.let { publicRanking(it) }.orEmpty()
    val best = visibleRanking.firstOrNull()

    fun revealReferralCode() {
        funnelState = funnelState.copy(
            codeVisible = true,
            referralDiscountEnabled = true,
        )
        val apecTotalRub = comparison?.scenarios
            ?.singleOrNull { it.id == calculator.ScenarioId.APEC }
            ?.totalRub
            ?.roundToLong()
            ?.toString()
            .orEmpty()
        val eVisaTotalRub = comparison?.scenarios
            ?.singleOrNull { it.id == calculator.ScenarioId.E_VISA }
            ?.totalRub
            ?.roundToLong()
            ?.toString()
            .orEmpty()
        metricsService.track("referral_code_click", "show_code", best?.title.orEmpty(), apecTotalRub, eVisaTotalRub)
        scope.launch {
            try {
                eventService.record(
                    FunnelEvent(
                        eventType = "referral_code_click",
                        sessionId = sessionId,
                        campaignId = campaignConfig.campaignId,
                        payloadJson = """{"bestScenario":"${best?.title.orEmpty()}"}""",
                    ),
                )
            } catch (_: Throwable) {
                // Local calculator behavior must remain available if event delivery fails.
            }
        }
    }

    Div({ classes("app-shell") }) {
        Div({ classes("header") }) {
            Div {
                H1 { Text("Калькулятор карты АТЭС") }
                P {
                    Text("Сравнение штампа и АТЭС с учетом цены визарана, курса, инфляции и потерянного рабочего дня.")
                }
            }
            Div({ classes("summary-card") }) {
                Span({ classes("summary-label") }) { Text("Лучший вариант") }
                Span({ classes("summary-value") }) { Text(best?.title ?: "Проверьте поля") }
                Span({ classes("summary-total") }) {
                    Text(best?.let { "${formatRub(it.totalRub)} за период" } ?: "Исправьте ошибки для расчета")
                }
            }
        }

        Div({ classes("layout") }) {
            Div {
                ReferralDiscountPanel(
                    baseCardCostValue = form.value("apecCardCostRub"),
                    cardCostError = parsed.errors.singleOrNull { it.fieldKey == "apecCardCostRub" },
                    effectiveCardCostRub = effectiveInput?.apecCardCostRub,
                    campaignConfig = campaignConfig,
                    codeVisible = funnelState.codeVisible,
                    referralDiscountEnabled = funnelState.referralDiscountEnabled,
                    onBaseCardCostChange = { form = form.update("apecCardCostRub", it) },
                    onRevealCode = ::revealReferralCode,
                    onDiscountEnabledChange = {
                        funnelState = funnelState.copy(referralDiscountEnabled = it)
                    },
                )
                InputPanel(
                    form = form,
                    fieldErrors = fieldErrors,
                    onFormChange = { form = it },
                )
                Div({ classes("preset-row") }) {
                    defaultCalculationPresets.forEach { preset ->
                        Div({ classes("preset-item") }) {
                            Button({
                                classes("secondary-button")
                                onClick { form = CalculatorFormState.fromInput(preset.input) }
                            }) {
                                Text(preset.title)
                            }
                            P({ classes("preset-description") }) {
                                Text(preset.description)
                            }
                        }
                    }
                }
            }

            Div({ classes("panel", "results-panel") }) {
                H2 { Text("Сводная таблица") }
                if (comparison == null) {
                    P { Text("Исправьте поля с ошибками, чтобы увидеть расчет.") }
                } else {
                    ResultsSection(visibleScenarios)

                    Div({ classes("insights") }) {
                        Div({ classes("insight") }) {
                            Span({ classes("summary-label") }) { Text("Рейтинг") }
                            Ul({ classes("ranking-list") }) {
                                visibleRanking.forEachIndexed { index, scenario ->
                                    Li {
                                        Text("${index + 1}. ${scenario.title} (${formatRub(scenario.totalRub)})")
                                    }
                                }
                            }
                        }
                        Div({ classes("insight") }) {
                            Span({ classes("summary-label") }) { Text("Что сравниваем") }
                            P {
                                Text("Публичный расчет показывает штамп и АТЭС: обычный визаран против карты с более длинным сроком пребывания и реферальной скидкой.")
                            }
                        }
                    }

                    ReferralFunnel(
                        comparison = comparison,
                        leadForm = leadForm,
                        funnelState = funnelState,
                        onLeadFormChange = { leadForm = it },
                        onFunnelStateChange = { funnelState = it },
                        metricsService = metricsService,
                        campaignConfig = campaignConfig,
                        leadService = leadService,
                        eventService = eventService,
                        sessionId = sessionId,
                    )

                    effectiveInput?.let { input ->
                        FormulaPanel(input, visibleScenarios)
                    }
                    SeoContent()
                }
            }
        }
    }
}

private fun browserSessionId(): String {
    val key = "apec-card-calculator-session-id"
    val existing = window.localStorage.getItem(key)
    if (!existing.isNullOrBlank()) return existing
    val created = "session-${js("Date.now()")}-${js("Math.random().toString(36).slice(2)")}"
    window.localStorage.setItem(key, created)
    return created
}
